import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  Plus, 
  Ticket, 
  Trophy,
  Play,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRaffles() {
  const [loading, setLoading] = useState(true);
  const [raffles, setRaffles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRaffle, setNewRaffle] = useState({
    name: '',
    description: '',
    prize_amount: 1000000,
    total_tickets: 100,
    ticket_price: 2000,
  });

  const fetchRaffles = async () => {
    try {
      const data = await api.admin.getRaffles();
      setRaffles(data);
    } catch (error) {
      console.error('Error fetching raffles:', error);
      toast.error('Error al cargar sorteos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRaffles();
  }, []);

  const handleCreateRaffle = async () => {
    if (!newRaffle.name) {
      toast.error('Ingresa un nombre para el sorteo');
      return;
    }

    setCreating(true);
    try {
      await api.admin.createRaffle(newRaffle);
      toast.success('Sorteo creado exitosamente');
      setDialogOpen(false);
      setNewRaffle({
        name: '',
        description: '',
        prize_amount: 1000000,
        total_tickets: 100,
        ticket_price: 2000,
      });
      fetchRaffles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear sorteo';
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleExecuteDraw = async (raffleId) => {
    if (!window.confirm('¿Ejecutar el sorteo ahora?')) return;
    
    try {
      const result = await api.admin.executeDraw(raffleId);
      toast.success(`Ganador: ${result.winning_number}`);
      fetchRaffles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al ejecutar sorteo';
      toast.error(message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="page-container pt-6 space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-32 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container pt-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="heading-2">Sorteos</h1>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-raffle-btn">
              <Plus className="w-5 h-5 mr-2" />
              Crear
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle>Crear Nuevo Sorteo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nombre del sorteo</Label>
                <Input
                  value={newRaffle.name}
                  onChange={(e) => setNewRaffle({ ...newRaffle, name: e.target.value })}
                  placeholder="Ej: Sorteo TuPrima #2"
                  className="input-base"
                  data-testid="raffle-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={newRaffle.description}
                  onChange={(e) => setNewRaffle({ ...newRaffle, description: e.target.value })}
                  placeholder="Descripción del sorteo"
                  className="input-base"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Premio (COP)</Label>
                  <Input
                    type="number"
                    value={newRaffle.prize_amount}
                    onChange={(e) => setNewRaffle({ ...newRaffle, prize_amount: parseInt(e.target.value) })}
                    className="input-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Total tickets</Label>
                  <Input
                    type="number"
                    value={newRaffle.total_tickets}
                    onChange={(e) => setNewRaffle({ ...newRaffle, total_tickets: parseInt(e.target.value) })}
                    className="input-base"
                  />
                </div>
              </div>

              <Button
                onClick={handleCreateRaffle}
                disabled={creating}
                className="w-full btn-primary"
                data-testid="confirm-create-raffle"
              >
                {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Crear Sorteo'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Raffles List */}
      {raffles.length === 0 ? (
        <Card className="card-base text-center py-12">
          <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="heading-4 mb-2">No hay sorteos</h2>
          <p className="text-muted-foreground mb-4">
            Crea el primer sorteo para comenzar
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {raffles.map((raffle) => (
            <Card key={raffle.id} className="card-base" data-testid={`raffle-card-${raffle.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{raffle.name}</CardTitle>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    raffle.status === 'active' ? 'bg-success/10 text-success' :
                    raffle.status === 'countdown' ? 'bg-accent/10 text-accent' :
                    'bg-slate-100 text-slate-500'
                  }`}>
                    {raffle.status === 'active' ? 'Activo' :
                     raffle.status === 'countdown' ? 'Cuenta regresiva' :
                     'Completado'}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Premio</p>
                    <p className="font-bold text-primary">{formatCurrency(raffle.prize_amount)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Vendidos</p>
                    <p className="font-bold">{raffle.sold_tickets} / {raffle.total_tickets}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Progreso</p>
                    <p className="font-bold">{raffle.progress_percentage.toFixed(1)}%</p>
                  </div>
                </div>

                {raffle.status === 'completed' && raffle.winner_ticket && (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                    <Trophy className="w-5 h-5 text-amber-500" />
                    <span className="text-sm">
                      Ganador: <span className="font-mono font-bold">{raffle.winner_ticket}</span>
                    </span>
                  </div>
                )}

                {raffle.status !== 'completed' && raffle.sold_tickets > 0 && (
                  <Button
                    onClick={() => handleExecuteDraw(raffle.id)}
                    variant="outline"
                    className="w-full rounded-full"
                    data-testid={`execute-draw-${raffle.id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Ejecutar Sorteo
                  </Button>
                )}

                <p className="text-xs text-muted-foreground">
                  Creado: {formatDate(raffle.created_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
